import { FC, useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useGlobal } from 'hooks/useGlobal';
import Form from 'react-bootstrap/Form';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { ApolloError, useMutation } from '@apollo/client';
import DeleteIcon from '@mui/icons-material/Delete';

import { Annotation } from 'common/graphqlTypes';
import * as K from 'common/constants';
import { clearGraphQLErrorMessage } from 'common/util/string';
import ELEMENT from 'common/element';
import { queryTimeRangeState } from 'components/common/Managers/RouteStateManager.state';

import Dropdown from 'components/common/Dropdown/Dropdown';
import Dialog, { CustomDialogHeaderOrFooter } from 'components/common/Dialog/Dialog';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';

import { AnnotationStyles, annotationStylesState } from 'styles/stylesProvider';
import {
  ADD_ANNOTATION,
  ANNOTATIONS_QUERY,
  DELETE_ANNOTATION,
  UPDATE_ANNOTATION,
} from './queries';

import './AnnotationModal.scss';

enum AnnotationModalVariant {
  CREATE = 'create',
  UPDATE = 'update',
}

type AnnotationModalProps = {
  showDialog: boolean;
  annotation: Annotation | null;
  onClose: () => void;
};

type FormValues = {
  annotationText: string;
};

const initialValue: FormValues = {
  annotationText: '',
};

const validationSchema = yup.object({
  annotationText: yup.string()
    .min(10, 'Please enter at least 10 characters for the annotation text.')
    .max(100, 'Must be at most 100 characters')
    .required('Please enter an annotation text.'),
});

const AnnotationModal: FC<AnnotationModalProps> = ({
  showDialog, onClose, annotation,
}: AnnotationModalProps) =>  {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const [selectionRange, setSelectionRange] = useGlobal(K.GLOBAL_KEY.CHART_SELECTION_RANGE);
  const { startTime, endTime } = useRecoilValue(queryTimeRangeState);
  const annotationsStyles = useRecoilValue(annotationStylesState);
  const [currentColor, setCurrentColor] = useState<AnnotationStyles>(annotationsStyles[0]);
  const [modalVariant, setModalVariant] = useState<AnnotationModalVariant>(AnnotationModalVariant.CREATE);

  const actionOptions = {
    refetchQueries: [
      {
        query: ANNOTATIONS_QUERY,
        variables: {
          startMs: parseInt(startTime, 10),
          endMs: parseInt(endTime, 10),
        },
      },
    ],
    onError: (error: ApolloError): void => {
      setPageAlertConfig(
        {
          message: clearGraphQLErrorMessage(error.message),
          variant: PageAlertVariant.ERROR,
          autoHide: true,
        },
      );
    },
  };
  const [addAnnotationMutation] = useMutation(ADD_ANNOTATION, actionOptions);
  const [updateAnnotationMutation] = useMutation(UPDATE_ANNOTATION, actionOptions);
  const [deleteAnnotationMutation] = useMutation(DELETE_ANNOTATION, actionOptions);

  const handleClose = (): void => {
    // reset local modal state
    resetForm(); // eslint-disable-line @typescript-eslint/no-use-before-define
    setCurrentColor(annotationsStyles[0]);
    setModalVariant(AnnotationModalVariant.CREATE);

    // reset related global selection state
    setSelectionRange({ selectionBox: false });

    onClose();
  };

  const handleDelete = (): void => {
    // deleteAnnotationMutation({ variables: { id: annotation?.id } });
    deleteAnnotationMutation(); // TODO: should be ID, waiting for ID to be added to schema
    handleClose();
  };
  const handleAnnotate = (values: FormValues): void => {
    if (modalVariant === AnnotationModalVariant.UPDATE) {
      updateAnnotationMutation({
        variables: {
          annotation: {
            // id: annotation?.id, // TODO: waiting for ID to be added to schema
            color: currentColor.borderColor,
            comment: values.annotationText,
          },
        },
      });
    } else {
      addAnnotationMutation({
        variables: {
          annotation: {
            color: currentColor.borderColor,
            comment: values.annotationText,
            startMs: selectionRange.start,
            endMs: selectionRange.end,
            labels: [{ key: 'source', value: 'scalyrUI' }],
          },
        },
      });
    }

    handleClose();
  };

  const {
    values, touched, errors, handleChange, submitForm, resetForm, setFieldValue,
  } = useFormik({
    initialValues: initialValue, onSubmit: handleAnnotate, validationSchema,
  });

  // Initialize initial values if we're editing an alert
  useEffect(() => {
    if (!annotation) { return; }

    const matchingColor = annotationsStyles.find(
      (colorItem) => colorItem.borderColor === annotation.color,
    ) || annotationsStyles[0];

    setModalVariant(AnnotationModalVariant.UPDATE);
    setCurrentColor(matchingColor);
    setFieldValue('annotationText', annotation.comment);
  }, [annotation, setFieldValue]);

  const selectionItems = [annotationsStyles.map((colorItem: AnnotationStyles) => {
    const item = (
      <span className="Annotation__Selection">
        <span className="Annotation__Square" style={{ borderColor: colorItem.borderColor, backgroundColor: colorItem.backgroundColor }} />
        {colorItem.name}
      </span>
    );
    return { children: item, clickHandler: (): void => setCurrentColor(colorItem) };
  })];

  const footer: CustomDialogHeaderOrFooter = {
    rightItems: [
      <Button
        variant={ButtonVariant.PRIMARY}
        onClick={submitForm}
      >
        Annotate
      </Button>,
      <Button onClick={handleClose}>
        Cancel
      </Button>,
    ],
  };

  if (modalVariant === AnnotationModalVariant.UPDATE) {
    footer.leftItems = [
      <Button onClick={handleDelete} leftIcon={<DeleteIcon fontSize="small" />}>
        Delete
      </Button>,
    ];
  }

  return (
    <Dialog
      show={showDialog}
      close={handleClose}
      closeBeforeActions={false}
      title="Annotate"
      cancelCallback={handleClose}
      onEnterCallback={submitForm}
      testId={ELEMENT.addAnnotationDialog}
      footer={footer}
    >
      <Form.Group className="Annotation__Group">
        <Form.Label>Annotation Text</Form.Label>
        <Form.Control
          placeholder="Add some annotation text here"
          value={values.annotationText}
          type="text"
          name="annotationText"
          isInvalid={touched.annotationText && !!errors.annotationText}
          onChange={handleChange}
          data-test-id={ELEMENT.addAnnotationDialogInput}
        />
        <Form.Control.Feedback type="invalid">{errors.annotationText}</Form.Control.Feedback>
      </Form.Group>
      <Form.Group>
        <Form.Label>Color</Form.Label>
        <Dropdown
          itemAs="button"
          sections={selectionItems}
          size="med"
          menuClass="Annotation__SelectionItems"
          className="Annotation__SelectionButton"
          buttonStyle={{ borderColor: currentColor.borderColor, backgroundColor: currentColor.backgroundColor }}
          testId={ELEMENT.addAnnotationDialogColor}
        >
          {currentColor.name}
        </Dropdown>
      </Form.Group>
    </Dialog>
  );
};

export default AnnotationModal;
